import { CUSTOM_STYLE_INJECTION_KEYS } from '@/shared/constants/custom-styles';
import { injectStylesheetLink, resolveCustomCssUrl } from '@/utils/custom-style-resources';

export type ClientCodeStyleConfig = {
  defaultTheme: string;
  defaultCustomCss?: string;
  loadLocalStyles: () => Promise<unknown>;
};

/** Client codes: lowercase, start with alnum or `_`, max 32 chars. Reserved names blocked. */
const CLIENT_CODE_PATTERN = /^[a-z0-9_][a-z0-9_-]{0,31}$/;
const RESERVED_CLIENT_CODES = new Set(['__proto__', 'constructor', 'prototype']);

let clientCodeStyleConfig: Record<string, ClientCodeStyleConfig> = Object.create(null);

/** Supply per-customer bundled style loaders from the host app (Vite root). */
export function configureClientCodeStyles(config: Record<string, ClientCodeStyleConfig>) {
  clientCodeStyleConfig = Object.assign(Object.create(null), config ?? {});
}

export function getClientCodeStyleConfig() {
  return clientCodeStyleConfig;
}

let loadedClientCode: string | null = null;
let loadClientStylesPromise: Promise<void> | null = null;

export function normalizeClientCode(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const normalized = value.trim().toLowerCase();

  if (RESERVED_CLIENT_CODES.has(normalized) || !CLIENT_CODE_PATTERN.test(normalized)) {
    return null;
  }

  return normalized;
}

export function resolveClientCodeDefaultTheme(clientCode: unknown): string | null {
  const normalized = normalizeClientCode(clientCode);
  return normalized && Object.hasOwn(clientCodeStyleConfig, normalized) ? (clientCodeStyleConfig[normalized]?.defaultTheme ?? null) : null;
}

export function resolveClientCodeCustomCss(apiBaseUrl: unknown, clientCode: unknown, remoteCustomCss: unknown) {
  const remoteUrl = resolveCustomCssUrl(apiBaseUrl, remoteCustomCss);

  if (remoteUrl) {
    return remoteUrl;
  }

  const normalized = normalizeClientCode(clientCode);
  const fallback =
    normalized && Object.hasOwn(clientCodeStyleConfig, normalized) ? clientCodeStyleConfig[normalized]?.defaultCustomCss : null;

  return resolveCustomCssUrl(apiBaseUrl, fallback);
}

async function loadLocalClientStyles(clientCode: unknown) {
  const normalized = normalizeClientCode(clientCode);

  if (!normalized) {
    loadedClientCode = null;
    loadClientStylesPromise = null;
    return;
  }

  if (loadedClientCode === normalized && loadClientStylesPromise) {
    return loadClientStylesPromise;
  }

  const config = Object.hasOwn(clientCodeStyleConfig, normalized) ? clientCodeStyleConfig[normalized] : undefined;

  // No bundled overlay is the API-driven path: app_client_code still names the
  // tenant, but styles come from app_theme / custom_css rather than config/{code}.
  if (!config?.loadLocalStyles) {
    loadedClientCode = normalized;
    loadClientStylesPromise = Promise.resolve();
    return loadClientStylesPromise;
  }

  loadedClientCode = normalized;
  loadClientStylesPromise = config.loadLocalStyles().catch((error) => {
    console.warn(`[settings] Failed to load local client styles for "${clientCode}":`, error);
  }) as Promise<void>;

  return loadClientStylesPromise;
}

export async function applyClientCodeStyleLayers({
  clientCode,
  customCss,
  apiBaseUrl,
}: {
  clientCode: unknown;
  customCss?: unknown;
  apiBaseUrl: unknown;
}) {
  const normalized = normalizeClientCode(clientCode);

  if (!normalized) {
    return;
  }

  await loadLocalClientStyles(normalized);

  const { CUSTOM_CSS } = CUSTOM_STYLE_INJECTION_KEYS;

  injectStylesheetLink(CUSTOM_CSS, resolveClientCodeCustomCss(apiBaseUrl, normalized, customCss), {
    dataset: { customStyleSource: 'remote', clientCode: normalized },
    bustCache: true,
  });
}

export async function loadClientCodeStyles(clientCode: unknown) {
  return loadLocalClientStyles(clientCode);
}

export function resetClientCodeStylesState() {
  loadedClientCode = null;
  loadClientStylesPromise = null;
  clientCodeStyleConfig = Object.create(null);
}
